import { apiClient } from "./client";
import { PaginationMeta } from "../lib/bufferedPagination";

export type RoleAdmin = {
  _id: string;
  name?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
};

export type RoleSummary = {
  id: string;
  name: string;
  description?: string;
  permissions?: string[];
  admins?: RoleAdmin[];
};

export type GetRolesParams = {
  startDate: string;
  endDate: string;
  limit?: number;
  sort?: string;
  q?: string;
  page?: number;
};

export type CreateRolePayload = {
  name: string;
  description: string;
  permissions: string[];
};

export type UpdateRolePayload = {
  name: string;
  description: string;
  permissions: string[];
};

export type DeleteRolePayload = {
  roleId?: string;
};

type RoleApiResponse = {
  _id: string;
  name: string;
  description?: string;
  permissions?: string[];
  admins?: RoleAdmin[];
};

type RolesResponse = {
  data?: RoleApiResponse[];
  roles?: RoleApiResponse[];
  items?: RoleApiResponse[];
  results?: RoleApiResponse[];
  meta?: PaginationMeta;
};

export type RolesResult = {
  items: RoleSummary[];
  meta?: PaginationMeta;
};

const normalizeRoles = (payload: RolesResponse | RoleApiResponse[]): RolesResult => {
  const list = Array.isArray(payload)
    ? payload
    : payload.data ?? payload.roles ?? payload.items ?? payload.results ?? [];

  return {
    items: list.map((role) => ({
      id: role._id,
      name: role.name,
      description: role.description,
      permissions: role.permissions ?? [],
      admins: role.admins ?? [],
    })),
    meta: Array.isArray(payload) ? undefined : payload.meta,
  };
};

export const getRoles = async (params: GetRolesParams) => {
  const response = await apiClient.get<RolesResponse | RoleApiResponse[]>(
    "/v1/admin/roles",
    { params },
  );

  return normalizeRoles(response.data ?? []);
};

export const createRole = async (payload: CreateRolePayload) => {
  await apiClient.post("/v1/admin/roles", payload);
};

export const updateRole = async (roleId: string, payload: UpdateRolePayload) => {
  await apiClient.put(`/v1/admin/roles/${roleId}`, payload);
};

export const deleteRole = async (roleId: string, payload?: DeleteRolePayload) => {
  await apiClient.delete(`/v1/admin/roles/${roleId}`, {
    data: payload,
  });
};
